import { z } from "zod"

import { tenantIdSchema } from "./identifiers.js"
import { platformCommandEnvelopeV1Schema } from "./platform-command-v1.js"

const timestampSchema = z.iso.datetime({ offset: true })
const versionSchema = z.string().regex(/^[0-9]+\.[0-9]+\.[0-9]+$/)
const schemaVersionSchema = z.string().regex(/^[0-9]{4}$/)
const siteNameSchema = z.string().regex(/^[a-z0-9][a-z0-9.-]{2,127}$/)
const codeSchema = z.string().regex(/^[a-z][a-z0-9_.-]{2,63}$/)

export const releaseCohortKindV1Schema = z.enum(["internal", "canary", "staged"])
export const releaseCohortStateV1Schema = z.enum([
  "planned", "ready", "deploying", "verifying", "succeeded", "failed", "halted",
])
export const releaseGateStateV1Schema = z.enum(["ready", "blocked", "unknown"])
export const releaseTargetStateV1Schema = z.enum([
  "pending", "deploying", "verifying", "succeeded", "failed", "rolled_back",
])

export const releaseVersionPinsV1Schema = z.object({
  appVersion: versionSchema,
  schemaVersion: schemaVersionSchema,
  configVersion: versionSchema,
}).strict()

export const releaseTargetV1Schema = z.object({
  targetTenantId: tenantIdSchema,
  siteName: siteNameSchema,
  environment: z.enum(["internal", "canary", "production"]),
  currentVersions: releaseVersionPinsV1Schema,
  compatibility: releaseGateStateV1Schema,
  backup: releaseGateStateV1Schema,
  backupCreatedAt: timestampSchema.nullable(),
  health: releaseGateStateV1Schema,
  capacity: releaseGateStateV1Schema,
  providerReadiness: releaseGateStateV1Schema,
  state: releaseTargetStateV1Schema,
  stableCode: codeSchema.nullable(),
}).strict().superRefine((target, context) => {
  if (
    (target.backup === "ready" && target.backupCreatedAt === null)
    || (target.backup !== "ready" && target.backupCreatedAt !== null)
  ) {
    context.addIssue({
      code: "custom",
      path: ["backupCreatedAt"],
      message: "Fresh-backup evidence must agree with its gate.",
    })
  }
})

export const releaseFeatureControlV1Schema = z.object({
  key: codeSchema,
  kind: z.enum(["feature_flag", "kill_switch"]),
  enabled: z.boolean(),
  reasonCode: codeSchema,
  changedAt: timestampSchema,
  changedByPlatformOperatorId: z.uuid(),
}).strict()

const releaseCohortSnapshotBaseV1Schema = z.object({
  contractVersion: z.literal(1),
  cohortId: z.uuid(),
  name: z.string().trim().min(3).max(80),
  kind: releaseCohortKindV1Schema,
  state: releaseCohortStateV1Schema,
  targetVersions: releaseVersionPinsV1Schema,
  targets: z.array(releaseTargetV1Schema).min(1).max(100),
  featureControls: z.array(releaseFeatureControlV1Schema).max(64),
  recoveryDecision: z.enum(["rollback", "forward"]).nullable(),
  haltCode: codeSchema.nullable(),
  revision: z.number().int().positive(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  valueFree: z.literal(true),
}).strict()

export const releaseCohortSnapshotV1Schema = releaseCohortSnapshotBaseV1Schema.superRefine((cohort, context) => {
  if (new Set(cohort.targets.map((target) => target.targetTenantId)).size !== cohort.targets.length) {
    context.addIssue({ code: "custom", path: ["targets"], message: "Release targets must be unique." })
  }
  if (
    (cohort.kind === "internal" && cohort.targets.some((target) => target.environment !== "internal"))
    || (cohort.kind === "canary" && cohort.targets.some((target) => target.environment !== "canary"))
    || (cohort.kind === "staged" && cohort.targets.some((target) => target.environment !== "production"))
  ) {
    context.addIssue({ code: "custom", path: ["targets"], message: "Release targets must match the cohort environment." })
  }
  if (
    (["failed", "halted"].includes(cohort.state) && cohort.haltCode === null)
    || (!["failed", "halted"].includes(cohort.state) && cohort.haltCode !== null)
  ) {
    context.addIssue({ code: "custom", path: ["haltCode"], message: "Halt evidence must agree with cohort state." })
  }
  if (cohort.recoveryDecision !== null && !["deploying", "verifying"].includes(cohort.state)) {
    context.addIssue({ code: "custom", path: ["recoveryDecision"], message: "Recovery decisions must resume controlled execution." })
  }
})

const planActionSchema = z.object({
  action: z.literal("plan"),
  cohort: releaseCohortSnapshotBaseV1Schema.omit({
    revision: true,
    createdAt: true,
    updatedAt: true,
  }),
}).strict()
const simpleAction = <T extends "mark_ready" | "start" | "halt">(
  action: T,
) => z.object({
  action: z.literal(action),
  cohortId: z.uuid(),
  reasonCode: codeSchema,
}).strict()
const recoverActionSchema = z.object({
  action: z.literal("recover"),
  cohortId: z.uuid(),
  decision: z.enum(["rollback", "forward"]),
  reasonCode: codeSchema,
}).strict()
const targetOutcomeActionSchema = z.object({
  action: z.literal("record_target_outcome"),
  cohortId: z.uuid(),
  targetTenantId: tenantIdSchema,
  state: z.enum(["deploying", "verifying", "succeeded", "failed"]),
  stableCode: codeSchema.nullable(),
}).strict().superRefine((action, context) => {
  if ((action.state === "failed") !== (action.stableCode !== null)) {
    context.addIssue({ code: "custom", path: ["stableCode"], message: "Failed target outcomes require one stable code." })
  }
})
const featureControlActionSchema = z.object({
  action: z.literal("set_feature_control"),
  cohortId: z.uuid(),
  key: codeSchema,
  kind: z.enum(["feature_flag", "kill_switch"]),
  enabled: z.boolean(),
  reasonCode: codeSchema,
}).strict()

export const manageReleaseCohortActionV1Schema = z.discriminatedUnion("action", [
  planActionSchema,
  simpleAction("mark_ready"),
  simpleAction("start"),
  simpleAction("halt"),
  recoverActionSchema,
  targetOutcomeActionSchema,
  featureControlActionSchema,
])

export const manageReleaseCohortV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema.safeExtend({
    commandType: z.literal("ManageReleaseCohort"),
  }),
  request: manageReleaseCohortActionV1Schema,
}).strict().superRefine((command, context) => {
  const cohortId = command.request.action === "plan"
    ? command.request.cohort.cohortId
    : command.request.cohortId
  const highRisk = command.request.action === "recover"
    || command.request.action === "start"
    || (command.request.action === "set_feature_control"
      && command.request.kind === "kill_switch")
  if (
    command.envelope.idempotencyNamespace !== "release.cohort.manage"
    || command.envelope.target.scope !== "platform"
    || command.envelope.target.targetTenantId !== null
    || command.envelope.target.targetType !== "release_cohort"
    || command.envelope.target.targetId !== cohortId
    || (highRisk && command.envelope.requiredApprovalIds.length < 1)
  ) {
    context.addIssue({ code: "custom", path: ["envelope"], message: "Release command must bind one exact cohort and applicable approval." })
  }
})

export const releaseCohortQueryV1Schema = z.object({
  envelope: z.object({
    contractVersion: z.literal(1),
    platformContextId: z.uuid(),
    platformOperatorId: z.uuid(),
    platformSessionId: z.uuid(),
    correlationId: z.uuid(),
    queryType: z.literal("ReleaseCohorts"),
    queryVersion: z.literal(1),
  }).strict(),
  kind: releaseCohortKindV1Schema.nullable(),
  state: releaseCohortStateV1Schema.nullable(),
  limit: z.number().int().min(1).max(100),
  cursor: z.uuid().nullable(),
}).strict()

export const releaseCohortPageV1Schema = z.object({
  responseVersion: z.literal(1),
  generatedAt: timestampSchema,
  items: z.array(releaseCohortSnapshotV1Schema).max(100),
  nextCursor: z.uuid().nullable(),
  partial: z.boolean(),
  valueFree: z.literal(true),
}).strict()

export const releaseCohortResponseV1Schema = z.object({
  responseVersion: z.literal(1),
  replayed: z.boolean(),
  snapshot: releaseCohortSnapshotV1Schema,
}).strict()

export type ReleaseCohortKindV1 = z.infer<typeof releaseCohortKindV1Schema>
export type ReleaseCohortStateV1 = z.infer<typeof releaseCohortStateV1Schema>
export type ReleaseTargetV1 = z.infer<typeof releaseTargetV1Schema>
export type ReleaseCohortSnapshotV1 = z.infer<typeof releaseCohortSnapshotV1Schema>
export type ReleaseCohortPageV1 = z.infer<typeof releaseCohortPageV1Schema>
export type ManageReleaseCohortV1 = z.infer<typeof manageReleaseCohortV1Schema>
export type ManageReleaseCohortActionV1 = z.infer<typeof manageReleaseCohortActionV1Schema>
